import { CloudOff, RefreshCw, TriangleAlert, Check, LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSession } from '@/auth/useSession';
import { useFormat } from '@/lib/format';
import { cn } from '@/lib/cn';
import { syncNow, useSyncStatus } from './client';

function useOnline(): boolean {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** PL-9: always visible — "Synced 08:42", "N pending", "Offline" or "Sync problem". */
export function SyncIndicator() {
  const { t } = useTranslation();
  const status = useSyncStatus();
  const session = useSession();
  const online = useOnline();
  const f = useFormat();
  let tone = 'bg-green-100 text-green-900';
  let icon = <Check className="h-4 w-4" />;
  let label = status.lastSyncAt ? t('sync.syncedAt', { time: f.time(status.lastSyncAt) }) : t('sync.notYet');
  let to = '/sync-issues';
  if (session.needsLogin || status.state === 'auth') {
    tone = 'bg-amber-100 text-amber-900';
    icon = <LogIn className="h-4 w-4" />;
    label = t('sync.loginToSync');
    to = '/login?resume=1';
  } else if (status.rejected > 0 || status.state === 'error') {
    tone = 'bg-red-100 text-red-900';
    icon = <TriangleAlert className="h-4 w-4" />;
    label = t('sync.problem');
  } else if (!online || status.state === 'offline') {
    tone = 'bg-stone-200 text-stone-800';
    icon = <CloudOff className="h-4 w-4" />;
    label = status.pending > 0 ? t('sync.offlinePending', { count: status.pending }) : t('sync.offline');
  } else if (status.state === 'syncing') {
    tone = 'bg-sky-100 text-sky-900';
    icon = <RefreshCw className="h-4 w-4 animate-spin" />;
    label = t('sync.syncing');
  } else if (status.pending > 0) {
    tone = 'bg-amber-100 text-amber-900';
    icon = <RefreshCw className="h-4 w-4" />;
    label = t('sync.pending', { count: status.pending });
  }
  return (
    <Link
      to={to}
      onClick={() => {
        if (to === '/sync-issues' && status.rejected === 0) void syncNow();
      }}
      className={cn('inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium', tone)}
      data-testid="sync-status"
      data-state={status.state}
      data-pending={status.pending}
    >
      {icon}
      <span className="max-w-40 truncate">{label}</span>
    </Link>
  );
}
